import type { ReactNode } from 'react'
import { Input } from '../../ui/input'
import { FieldInfo, useFieldContext, useFormReadOnly, useIsFieldOptional } from './shared'
import { FieldLabelRow } from './field-label-row'
import { HORIZONTAL_CONTROL_CLASS, HORIZONTAL_LABEL_CLASS, HORIZONTAL_ROW_CLASS } from './horizontal-field'

/**
 * The label row and read-only face shared by the native country/province fields and the grouped sector/function comboboxes.
 * Read-only, the control gives way to the same borderless text a read-only `TextField` shows, since a disabled
 * select greys out.
 */
export function NativeSelectFieldShell(props: {
  label: string
  className?: string
  optional?: boolean
  orientation?: 'vertical' | 'horizontal'
  readOnlyLabel: string | undefined
  children: ReactNode
}) {
  const field = useFieldContext<unknown>()
  const formReadOnly = useFormReadOnly()
  const isOptional = useIsFieldOptional(field.name, props.optional)
  const isHorizontal = props.orientation === 'horizontal'

  return (
    <div className={props.className}>
      <div className={isHorizontal ? HORIZONTAL_ROW_CLASS : 'grid gap-2'}>
        {props.label && (
          <FieldLabelRow
            label={props.label}
            htmlFor={field.name}
            hasError={field.state.meta.isTouched && !field.state.meta.isValid}
            optional={isOptional}
            compactOptional={isHorizontal}
            className={isHorizontal ? HORIZONTAL_LABEL_CLASS : undefined}
          />
        )}
        <div className={isHorizontal ? HORIZONTAL_CONTROL_CLASS : undefined}>
          {formReadOnly ? (
            <Input
              id={field.name}
              readOnly
              tabIndex={-1}
              value={props.readOnlyLabel ?? '-'}
              className="border-transparent bg-transparent px-0 focus-visible:border-transparent focus-visible:ring-0 dark:bg-transparent"
            />
          ) : (
            props.children
          )}
          <FieldInfo fieldState={field.state} />
        </div>
      </div>
    </div>
  )
}
